import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const EMPTY = { description: "", unit: "", unit_price: "" };
const eur = (v) => Number(v).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
const toNumber = (v) => Number(String(v).replace(",", "."));

function PriceForm({ initial, onSubmit, onCancel, submitLabel, testPrefix }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const price = toNumber(form.unit_price);
    if (!form.description.trim() || !form.unit.trim() || form.unit_price === "" || Number.isNaN(price) || price < 0) {
      toast.error("Compila descrizione, UM e un prezzo valido");
      return;
    }
    setSaving(true);
    const ok = await onSubmit({ description: form.description.trim(), unit: form.unit.trim(), unit_price: price });
    setSaving(false);
    if (ok && !onCancel) setForm(EMPTY);
  };

  return (
    <form onSubmit={submit} className="grid gap-3 sm:grid-cols-[1fr_7rem_9rem_auto] items-start" data-testid={`${testPrefix}-form`}>
      <textarea
        className="tm-textarea !min-h-[44px]"
        rows={2}
        placeholder="Descrizione completa prodotto"
        value={form.description}
        onChange={set("description")}
        data-testid={`${testPrefix}-description`}
      />
      <input className="tm-input" placeholder="UM" value={form.unit} onChange={set("unit")} data-testid={`${testPrefix}-unit`} />
      <input
        className="tm-input"
        inputMode="decimal"
        placeholder="Prezzo €"
        value={form.unit_price}
        onChange={set("unit_price")}
        data-testid={`${testPrefix}-price`}
      />
      <div className="flex gap-2">
        {onCancel && (
          <button type="button" onClick={onCancel} className="tm-btn-outline text-sm !px-3" data-testid={`${testPrefix}-cancel`}>
            Annulla
          </button>
        )}
        <button type="submit" disabled={saving} className="tm-btn-primary text-sm !px-4 disabled:opacity-50" data-testid={`${testPrefix}-submit`}>
          {saving ? "..." : submitLabel}
        </button>
      </div>
    </form>
  );
}

function PriceRow({ entry, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false);
  if (editing) {
    return (
      <div className="py-3">
        <PriceForm
          initial={{ description: entry.description, unit: entry.unit, unit_price: String(entry.unit_price).replace(".", ",") }}
          onSubmit={async (data) => { const ok = await onUpdate(entry, data); if (ok) setEditing(false); return ok; }}
          onCancel={() => setEditing(false)}
          submitLabel="Salva"
          testPrefix={`price-edit-${entry.id}`}
        />
      </div>
    );
  }
  return (
    <div className="py-3 grid gap-2 sm:grid-cols-[1fr_7rem_9rem_auto] items-center" data-testid={`price-row-${entry.id}`}>
      <div className="text-slate-200 text-sm whitespace-pre-line">{entry.description}</div>
      <div className="font-mono text-xs text-slate-400 uppercase">{entry.unit}</div>
      <div className="text-white font-medium">{eur(entry.unit_price)}</div>
      <div className="flex gap-3 font-mono text-xs uppercase tracking-widest">
        <button onClick={() => setEditing(true)} className="text-orange-400 hover:text-orange-300" data-testid={`price-edit-btn-${entry.id}`}>Modifica</button>
        <button onClick={() => onDelete(entry)} className="text-red-400 hover:text-red-300" data-testid={`price-delete-btn-${entry.id}`}>Elimina</button>
      </div>
    </div>
  );
}

export function PriceListEditor({ productId = null, onChanged }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/prices", { params: productId ? { product_id: productId } : { standalone: true } })
      .then((r) => setEntries(r.data))
      .catch(() => toast.error("Errore caricamento listini"))
      .finally(() => setLoading(false));
  }, [productId]);

  useEffect(() => { load(); }, [load]);

  const call = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      load();
      onChanged?.();
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || "Errore salvataggio");
      return false;
    }
  };

  const create = (data) => call(() => api.post("/admin/prices", { ...data, product_id: productId }), "Listino aggiunto");
  const update = (entry, data) => call(() => api.put(`/admin/prices/${entry.id}`, { ...data, product_id: entry.product_id }), "Listino aggiornato");
  const remove = (entry) => {
    if (!window.confirm("Eliminare questo listino?")) return;
    call(() => api.delete(`/admin/prices/${entry.id}`), "Listino eliminato");
  };

  return (
    <div data-testid="price-list-editor">
      <div className="hidden sm:grid grid-cols-[1fr_7rem_9rem_auto] gap-2 font-mono text-[10px] uppercase tracking-widest text-slate-500 pb-2 border-b border-slate-700">
        <span>Descrizione completa</span><span>UM</span><span>Prezzo unit. netto</span><span className="w-32" />
      </div>
      <div className="divide-y divide-slate-800 mb-6" data-testid="price-rows">
        {loading && <div className="py-4 text-slate-500 text-sm">Caricamento…</div>}
        {!loading && entries.length === 0 && (
          <div className="py-4 text-slate-500 text-sm" data-testid="price-empty">Nessun listino inserito.</div>
        )}
        {entries.map((e) => <PriceRow key={e.id} entry={e} onUpdate={update} onDelete={remove} />)}
      </div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-orange-500 mb-2">+ Nuovo listino</div>
      <PriceForm initial={EMPTY} onSubmit={create} submitLabel="Aggiungi" testPrefix="price-new" />
    </div>
  );
}

export function PriceListModal({ product, onClose, onChanged }) {
  return (
    <div className="tm-modal fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto" data-testid="price-modal">
      <div className="tm-card p-8 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <div className="tm-tag inline-block mb-3">Listino prezzi · solo admin</div>
            <h3 className="font-heading text-2xl text-white tracking-tighter">{product.name}</h3>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-200 text-2xl w-11 h-11 flex items-center justify-center" aria-label="Chiudi" data-testid="price-modal-close">✕</button>
        </div>
        <PriceListEditor productId={product.id} onChanged={onChanged} />
      </div>
    </div>
  );
}
